/** CRUD editor for dynamic report header/body/footer templates. */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getFormElements } from '../../api/forms-api';
import { createReport, getEditableReport, removeReport, updateReport } from '../../api/reports-api';
import { ErrorMessage, Loading } from '../../components/Loading';
import { useFeedback } from '../../components/Feedback';
import type { FormElementItem } from '../../models/form';
import type { DynamicReport, ReportTab } from '../../models/report';
import { sanitizeCustomHtml } from '../../utils/customization';

function defaultReport(formId: number): DynamicReport {
  return { ID: 0, FormID: formId, Title: 'گزارش جدید', Description: '', Tabs: [
    { Name: 'Header', Title: 'سربرگ', Body: '<div class="report-header"><h2>عنوان گزارش</h2></div>' },
    { Name: 'Body', Title: 'بدنه', Body: '<div class="report-body"><p>اطلاعات رکورد</p></div>' },
    { Name: 'Footer', Title: 'پابرگ', Body: '<div class="report-footer"></div>' }
  ] };
}

export function ReportDesignerPage() {
  const { notify } = useFeedback();
  const navigate = useNavigate();
  const params = useParams();
  const reportId = Number(params.id ?? 0);
  const formId = Number(params.formid ?? 0);
  const [report, setReport] = useState<DynamicReport>(() => defaultReport(formId));
  const [elements, setElements] = useState<FormElementItem[]>([]);
  const [activeTab, setActiveTab] = useState('Body');
  const [loading, setLoading] = useState(Boolean(reportId));
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<unknown>();
  const editorRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    getFormElements(formId).then(setElements).catch(setError);
    if (reportId) getEditableReport(reportId).then((loaded) => setReport({ ...defaultReport(formId), ...loaded, Tabs: loaded.Tabs?.length ? loaded.Tabs : defaultReport(formId).Tabs })).catch(setError).finally(() => setLoading(false));
  }, [formId, reportId]);

  const tab = useMemo(() => report.Tabs.find((item) => item.Name === activeTab) ?? report.Tabs[0], [activeTab, report.Tabs]);
  const patchTab = (changes: Partial<ReportTab>) => setReport((current) => ({ ...current, Tabs: current.Tabs.map((item) => item.Name === tab?.Name ? { ...item, ...changes } : item) }));
  const insertBinding = (element: FormElementItem) => {
    if (!tab) return;
    const binding = `{{form.${element.FullModel?.split('.').slice(-2).join('.') || element.Model}}}`;
    const editor = editorRef.current;
    const start = editor?.selectionStart ?? tab.Body.length;
    const end = editor?.selectionEnd ?? start;
    const nextBody = `${tab.Body.slice(0, start)}${binding}${tab.Body.slice(end)}`;
    patchTab({ Body: nextBody });
    window.requestAnimationFrame(() => {
      editorRef.current?.focus();
      const caret = start + binding.length;
      editorRef.current?.setSelectionRange(caret, caret);
    });
  };

  const save = async () => {
    if (saving || deleting) return;
    if (!report.Title.trim()) { notify('عنوان گزارش را وارد کنید.', 'warning'); return; }
    setSaving(true);
    try {
      if (report.ID) await updateReport(report); else { const id = await createReport(report); setReport((current) => ({ ...current, ID: id })); navigate(`/dynamicforms/reports/edit/${id}/formid/${formId}`, { replace: true }); }
      notify('گزارش ذخیره شد.', 'success');
    } catch (saveError) { notify(saveError instanceof Error ? saveError.message : 'خطا در ذخیره گزارش', 'error'); } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!report.ID || saving || deleting || !window.confirm('گزارش حذف شود؟')) return;
    setDeleting(true);
    try {
      await removeReport(report.ID);
      notify('گزارش حذف شد.', 'success');
      navigate('/dynamicforms/reports/customreporstlist');
    } catch (removeError) {
      notify(removeError instanceof Error ? removeError.message : 'خطا در حذف گزارش', 'error');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <Loading />;
  return <section className="df-report-designer">
    <header className="df-designer-toolbar"><button type="button" className="btn btn-success" disabled={saving || deleting} onClick={() => void save()}>{saving ? 'در حال ذخیره…' : 'ذخیره گزارش'}</button><button type="button" className="btn btn-default" disabled={saving || deleting} onClick={() => navigate('/dynamicforms/reports/customreporstlist')}>بازگشت</button>{report.ID ? <button type="button" className="btn btn-danger" disabled={saving || deleting} onClick={() => void remove()}>{deleting ? 'در حال حذف…' : 'حذف'}</button> : null}<strong>{report.ID ? `ویرایش گزارش ${report.ID}` : 'ایجاد گزارش'}</strong></header>
    {error ? <ErrorMessage error={error} /> : null}
    <div className="df-report-designer__layout">
      <aside className="df-report-sidebar"><div className="form-group"><label>عنوان</label><input className="form-control" value={report.Title} onChange={(event) => setReport((current) => ({ ...current, Title: event.target.value }))} /></div><div className="form-group"><label>توضیحات</label><textarea className="form-control" rows={5} value={report.Description} onChange={(event) => setReport((current) => ({ ...current, Description: event.target.value }))} /></div><hr/><h4>فیلدهای فرم</h4><p className="help-block">عبارت فیلد در محل نشانگر ویرایشگر درج می‌شود.</p><div className="df-binding-list">{elements.map((element) => <button type="button" key={element.FullModel || element.Model} onClick={() => insertBinding(element)}>{element.Title}<code>{element.Model}</code></button>)}</div></aside>
      <main className="df-report-editor"><ul className="nav nav-tabs">{report.Tabs.map((item) => <li key={item.Name} className={item.Name === tab?.Name ? 'active' : ''}><button type="button" onClick={() => setActiveTab(item.Name)}>{item.Title}</button></li>)}</ul>{tab ? <><textarea ref={editorRef} className="form-control df-html-editor" dir="ltr" spellCheck={false} value={tab.Body ?? ''} onChange={(event) => patchTab({ Body: event.target.value })} /><h4>پیش‌نمایش</h4><div className="df-report-preview" dangerouslySetInnerHTML={{ __html: sanitizeCustomHtml(tab.Body || '') }} /></> : null}</main>
    </div>
  </section>;
}
